import { useEffect, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { CheckCircle2, Loader2, MinusCircle, RotateCw, Target, X, XCircle } from "lucide-react";
import { errMsg } from "./paywall";

const md = (c) => (
  <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{c}</ReactMarkdown>
);

export const CompetitiveQuiz = ({ api, authConfig, exam, subject, chapter, onClose }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const examLabel = exam === "jee" ? "JEE Main" : "NEET";
  const genOnceRef = useRef(false);

  useEffect(() => {
    if (genOnceRef.current) return;
    genOnceRef.current = true;
    generate();
    // eslint-disable-next-line
  }, []);

  async function generate() {
    setBusy(true); setErr(""); setResult(null); setAnswers({});
    try {
      const r = await axios.post(`${api}/tutor/competitive/quiz`, { exam, subject, chapter: chapter || null }, authConfig);
      setQuiz(r.data.quiz);
    } catch (e) { setErr(errMsg(e, "Could not prepare the quiz.")); }
    finally { setBusy(false); }
  }

  async function submit() {
    setSubmitting(true); setErr("");
    try {
      const arr = quiz.questions.map((_, i) => (answers[i] === undefined ? -1 : answers[i]));
      const r = await axios.post(`${api}/tutor/quizzes/${quiz.quiz_id}/submit`, { answers: arr }, authConfig);
      setResult(r.data);
    } catch (e) { setErr(errMsg(e, "Submit failed.")); }
    finally { setSubmitting(false); }
  }

  const attempted = quiz ? Object.keys(answers).filter((k) => answers[k] !== undefined).length : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="comp-quiz-overlay">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-5 sm:p-6 relative">
        <button onClick={onClose} data-testid="comp-quiz-close-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={18}/></button>
        <p className="text-violet-300 text-xs font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
          <Target size={13}/> {examLabel} practice · +4 correct / −1 wrong / 0 skipped
        </p>
        <h3 className="text-white font-bold text-lg mb-4">{chapter || "Full Syllabus Mix"}</h3>

        {err && (
          <div className="mb-4">
            <p className="text-red-300 text-sm mb-3" data-testid="comp-quiz-error">⚠ {err}</p>
            <button onClick={generate} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-white hover:border-violet-400"><RotateCw size={12}/> Try again</button>
          </div>
        )}

        {busy ? (
          <div className="py-14 text-center">
            <Loader2 className="animate-spin text-violet-400 mx-auto mb-3" size={24}/>
            <p className="text-zinc-400 text-sm">Setting {examLabel}-level questions — tougher than NCERT…</p>
          </div>
        ) : result ? (
          <div data-testid="comp-quiz-result">
            <div className={`rounded-xl p-4 mb-5 text-center border ${result.wrong_count === 0 ? "bg-emerald-500/10 border-emerald-400/40" : "bg-violet-500/10 border-violet-400/30"}`}>
              <p className="text-3xl font-black text-white" data-testid="comp-quiz-score">{result.comp_score}/{result.comp_max}</p>
              <p className="text-zinc-300 text-sm mt-1">
                {examLabel} marking: {result.score} correct, {result.total - result.score - result.unattempted} wrong, {result.unattempted} skipped
              </p>
            </div>
            {result.rank_estimate && (
              <div className="rounded-xl border border-violet-400/30 bg-violet-500/10 p-4 mb-5 text-center" data-testid="rank-estimate">
                <p className="text-violet-200 text-sm font-black">
                  Est. Percentile {result.rank_estimate.percentile} · Top {result.rank_estimate.top_pct}% · {result.rank_estimate.label}
                </p>
                <p className="text-zinc-500 text-[11px] mt-1">{result.rank_estimate.note}</p>
              </div>
            )}
            <div className="space-y-3 mb-5">
              {result.results.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3.5 ${r.your_index === -1 ? "border-zinc-700 bg-zinc-800/20" : r.correct ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid={`comp-quiz-result-q${i}`}>
                  <div className="flex items-start gap-2 text-sm text-zinc-100">
                    {r.your_index === -1 ? <MinusCircle size={15} className="text-zinc-500 mt-0.5 shrink-0"/> : r.correct ? <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 shrink-0"/> : <XCircle size={15} className="text-red-400 mt-0.5 shrink-0"/>}
                    <div className="prose prose-invert prose-sm max-w-none [&_p]:my-0">{md(`**Q${i + 1}.** ${r.q}`)}</div>
                  </div>
                  {!r.correct && (
                    <div className="mt-2 ml-6 text-xs space-y-0.5">
                      {r.your_index >= 0 && <p className="text-red-300">Your answer: {r.options[r.your_index]}</p>}
                      <p className="text-emerald-300">Correct: {r.options[r.correct_index]}</p>
                      {r.explanation && <p className="text-zinc-400">{r.explanation}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={generate} data-testid="comp-quiz-again-btn"
                className="px-4 py-2.5 rounded-full text-sm font-bold text-white" style={{ background: "linear-gradient(135deg, #7C3AED, #A78BFA)" }}>
                Next 5 questions
              </button>
              <button onClick={onClose} data-testid="comp-quiz-done-btn" className="px-4 py-2.5 rounded-full text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500">Close</button>
            </div>
          </div>
        ) : quiz ? (
          <div data-testid="comp-quiz-questions">
            <div className="space-y-5 mb-5">
              {quiz.questions.map((q, i) => (
                <div key={i} data-testid={`comp-quiz-q${i}`}>
                  <div className="flex items-start gap-2 mb-2">
                    {q.qtype === "assertion" && <span className="text-[10px] bg-violet-500/20 text-violet-300 border border-violet-400/30 px-1.5 py-0.5 rounded-full font-bold shrink-0 mt-0.5">A-R</span>}
                    <div className="text-sm text-zinc-100 prose prose-invert prose-sm max-w-none [&_p]:my-0">{md(`**Q${i + 1}.** ${q.q}`)}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {q.options.map((opt, oi) => (
                      <button key={oi} onClick={() => setAnswers({ ...answers, [i]: answers[i] === oi ? undefined : oi })} data-testid={`comp-quiz-q${i}-opt${oi}`}
                        className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${answers[i] === oi
                          ? "bg-violet-500/15 border-violet-400 text-violet-100"
                          : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>
                        <span className="prose prose-invert prose-sm max-w-none [&_p]:my-0 [&_p]:inline">{md(opt)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-xs mb-2 text-center">Not sure? Leave it blank — skipped questions score 0, wrong ones cost −1.</p>
            <button onClick={submit} disabled={submitting || attempted === 0} data-testid="comp-quiz-submit-btn"
              className="w-full py-3 rounded-full text-sm font-bold text-white disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, #7C3AED, #A78BFA)" }}>
              {submitting ? <Loader2 size={14} className="animate-spin inline"/> : `Submit (${attempted}/${quiz.questions.length} attempted)`}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
